import type { Platform } from './types';

const API_BASE = import.meta.env.VITE_API_URL || '';

let apiContext: { initData: string; platform: Platform; startParam?: string } = {
  initData: 'dev_mode',
  platform: 'dev',
};

export function setApiContext(ctx: { initData: string; platform: Platform; startParam?: string }) {
  apiContext = ctx;
}

function encodeInitData(initData: string): string {
  const bytes = new TextEncoder().encode(initData);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function headers(adminPassword?: string): HeadersInit {
  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Init-Data': encodeInitData(apiContext.initData),
    'X-Init-Data-Encoding': 'base64',
    'X-Platform': apiContext.platform,
  };
  if (adminPassword) {
    h['X-Admin-Password'] = adminPassword;
  }
  return h;
}

async function request<T>(
  path: string,
  options: RequestInit & { adminPassword?: string } = {}
): Promise<T> {
  const { adminPassword, ...fetchOpts } = options;
  const res = await fetch(`${API_BASE}${path}`, {
    ...fetchOpts,
    headers: {
      ...headers(adminPassword),
      ...(fetchOpts.headers || {}),
    },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Ошибка ${res.status}`);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  getMe: () =>
    request<import('./types').MeResponse>('/api/me', {
      method: 'POST',
      body: JSON.stringify({
        initData: apiContext.initData,
        platform: apiContext.platform,
        startParam: apiContext.startParam,
      }),
    }),

  getQuestions: () => request<import('./types').Question[]>('/api/questions'),

  getEventByCode: (code: string) =>
    request<import('./types').EventInfo>(`/api/events/by-code/${code}`),

  getMyResponse: (eventId: number) =>
    request<{ submitted: boolean; lotteryNumber?: number; createdAt?: string }>(
      `/api/events/${eventId}/my-response`
    ),

  submitResponse: (eventCode: string, answers: Record<string, string>, pdConsent: boolean) =>
    request<import('./types').SubmitResult>('/api/responses', {
      method: 'POST',
      body: JSON.stringify({ eventCode, answers, pdConsent }),
    }),

  getPublicConfig: () => request<import('./types').PublicConfig>('/api/config/public'),

  adminGetEvents: (adminPassword?: string) =>
    request<import('./types').EventInfo[]>('/api/admin/events', { adminPassword }),

  adminCreateEvent: (name: string, eventDate: string, adminPassword?: string) =>
    request<import('./types').EventInfo>('/api/admin/events', {
      method: 'POST',
      body: JSON.stringify({ name, eventDate }),
      adminPassword,
    }),

  adminUpdateEvent: (
    id: number,
    data: { name?: string; isActive?: boolean; eventDate?: string },
    adminPassword?: string
  ) =>
    request<import('./types').EventInfo>(`/api/admin/events/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      adminPassword,
    }),

  adminDeleteEvent: (id: number, adminPassword?: string) =>
    request<void>(`/api/admin/events/${id}`, {
      method: 'DELETE',
      adminPassword,
    }),

  adminGetQuestions: (adminPassword?: string) =>
    request<import('./types').Question[]>('/api/admin/questions', { adminPassword }),

  adminCreateQuestion: (
    data: Partial<import('./types').Question> & { text: string },
    adminPassword?: string
  ) =>
    request<import('./types').Question>('/api/admin/questions', {
      method: 'POST',
      body: JSON.stringify(data),
      adminPassword,
    }),

  adminUpdateQuestion: (
    id: number,
    data: Partial<import('./types').Question>,
    adminPassword?: string
  ) =>
    request<import('./types').Question>(`/api/admin/questions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      adminPassword,
    }),

  adminDeleteQuestion: (id: number, adminPassword?: string) =>
    request<{ ok: boolean }>(`/api/admin/questions/${id}`, {
      method: 'DELETE',
      adminPassword,
    }),

  adminGetStats: (eventId: number, adminPassword?: string) =>
    request<import('./types').EventStats>(`/api/admin/events/${eventId}/stats`, {
      adminPassword,
    }),

  adminSendExcel: (eventId: number, adminPassword?: string) =>
    request<{ ok: boolean; message: string }>(`/api/admin/events/${eventId}/send-excel`, {
      method: 'POST',
      adminPassword,
    }),

  adminSendQrPdf: (eventId: number, adminPassword?: string) =>
    request<{ ok: boolean; message: string }>(`/api/admin/events/${eventId}/send-qr-pdf`, {
      method: 'POST',
      adminPassword,
    }),

  exportExcelUrl: (eventId: number) => `${API_BASE}/api/admin/events/${eventId}/export.xlsx`,

  qrImageUrl: (eventId: number) => `${API_BASE}/api/admin/events/${eventId}/qr.png`,

  qrPdfUrl: (eventId: number) => `${API_BASE}/api/admin/events/${eventId}/qr.pdf`,

  adminGetAccess: (adminPassword?: string) =>
    request<import('./types').AccessInfo>('/api/admin/access', { adminPassword }),

  adminAddAccessAdmin: (
    data: { platform: import('./types').Platform; userId: number; displayName?: string },
    adminPassword?: string
  ) =>
    request<import('./types').AdminUser>('/api/admin/access/admins', {
      method: 'POST',
      body: JSON.stringify(data),
      adminPassword,
    }),

  adminAddAccessAdminMe: (adminPassword?: string) =>
    request<import('./types').AdminUser>('/api/admin/access/admins/me', {
      method: 'POST',
      adminPassword,
    }),

  adminRemoveAccessAdmin: (id: number, adminPassword?: string) =>
    request<{ ok: boolean }>(`/api/admin/access/admins/${id}`, {
      method: 'DELETE',
      adminPassword,
    }),

  adminChangePassword: (
    data: { currentPassword?: string; newPassword: string },
    adminPassword?: string
  ) =>
    request<{ ok: boolean; message: string }>('/api/admin/access/password', {
      method: 'POST',
      body: JSON.stringify(data),
      adminPassword,
    }),
};

export function getApiPlatform(): Platform {
  return apiContext.platform;
}

export async function downloadExcel(
  eventId: number,
  eventName: string,
  adminPassword?: string
): Promise<string | void> {
  if (apiContext.platform === 'telegram' || apiContext.platform === 'max') {
    const result = await api.adminSendExcel(eventId, adminPassword);
    return result.message;
  }

  const res = await fetch(api.exportExcelUrl(eventId), {
    headers: headers(adminPassword),
  });
  if (!res.ok) throw new Error('Не удалось выгрузить Excel');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `avicenna_${eventName}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function downloadQrPdf(
  eventId: number,
  eventName: string,
  adminPassword?: string
): Promise<string | void> {
  if (apiContext.platform === 'telegram' || apiContext.platform === 'max') {
    const result = await api.adminSendQrPdf(eventId, adminPassword);
    return result.message;
  }

  const res = await fetch(api.qrPdfUrl(eventId), {
    headers: headers(adminPassword),
  });
  if (!res.ok) throw new Error('Не удалось скачать PDF');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `avicenna_qr_${eventName}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
}

export async function fetchQrBlob(eventId: number, adminPassword?: string): Promise<string> {
  const res = await fetch(api.qrImageUrl(eventId), {
    headers: headers(adminPassword),
  });
  if (!res.ok) throw new Error('Не удалось загрузить QR-код');
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
